import React, { useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
  Tooltip
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import Header from '../../global/Header';
import { tokens } from '../../../../theme';
import { useDiscreteMode } from '../../global/DiscreteMode';

import { useGetApisUsageSummaryQuery } from '../../../../features/organizations/organizationsApiSlice';
import { useNavigate } from 'react-router-dom';

const numberFormat = new Intl.NumberFormat();

const pct = (used, limit) => {
  const u = Number(used || 0);
  const l = Number(limit || 0);
  if (!l) return 0;
  return u / l;
};

const fmtEpoch = (epoch) => {
  if (!epoch) return '';
  const d = new Date(Number(epoch) * 1000);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().replace('T', ' ').slice(0, 16) + 'Z';
};

const daysForRange = (range) => {
  if (range === 'day') return 1;
  if (range === '7d') return 7;
  if (range === '30d') return 30;
  return null; // all
};

const rangeEquivalentLimit = ({ ledgerPeriod, creditsLimit, range }) => {
  const period = String(ledgerPeriod || '').toUpperCase();
  const limit = Number(creditsLimit || 0);
  const days = daysForRange(range);

  if (!limit) return 0;

  // For "all", there's no meaningful window limit
  if (!days) return 0;

  if (period === 'DAY') return limit * days;

  // MONTH (or unknown): prorate to the window (assume 30-day month)
  return limit * (days / 30);
};

const planLabel = ({ ledgerPeriod, creditsLimit }) => {
  const period = String(ledgerPeriod || '').toUpperCase();
  const limit = Number(creditsLimit || 0);
  if (!limit) return '—';
  if (period === 'DAY') return `${numberFormat.format(limit)}/day`;
  if (period === 'MONTH') return `${numberFormat.format(limit)}/month`;
  return `${numberFormat.format(limit)} (${period || 'period'})`;
};

const ApiCountSummaries = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);


  const { isDiscreteMode } = useDiscreteMode();
  const navigate = useNavigate();

  // day | 7d | 30d | all
  const [range, setRange] = useState('7d');
  const [orgSearch, setOrgSearch] = useState('');

  const { data, isLoading, isError } = useGetApisUsageSummaryQuery(
    { range },
    { pollingInterval: 15000, refetchOnFocus: true, refetchOnMountOrArgChange: true }
  );

  const rowsRaw = data?.rows || [];

  const filteredRows = useMemo(() => {
    const q = (orgSearch || '').toLowerCase().trim();
    if (!q) return rowsRaw;
    return rowsRaw.filter((r) => {
      const name = String(r.org_name || '').toLowerCase();
      const id = String(r.org_id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [rowsRaw, orgSearch]);

  const rows = useMemo(() => {
    return filteredRows.map((r) => {
      const creditsLimit = Number(r.credits_limit ?? 0);
      const period = String(r.period || '').toUpperCase();

      const usedRange =
        range === 'day' ? Number(r.used_day ?? 0) :
          range === '7d' ? Number(r.used_7d ?? 0) :
            range === '30d' ? Number(r.used_30d ?? 0) :
              Number(r.used_all ?? 0);

      const limitRangeEq = rangeEquivalentLimit({
        ledgerPeriod: period,
        creditsLimit,
        range,
      });

      // For all-time: % is not meaningful. Use current ledger usage as a fallback,
      // or just show 0/blank. Here: show current-period %.
      const pctDenom = range === 'all' ? creditsLimit : limitRangeEq;
      const pctNumer = range === 'all' ? Number(r.credits_used ?? 0) : usedRange;

      return {
        id: String(r.org_id),
        org_id: String(r.org_id),
        org_name: String(r.org_name || ''),

        period,
        credits_limit: creditsLimit,
        plan_label: planLabel({ ledgerPeriod: period, creditsLimit }),

        // window totals
        used_range: usedRange,
        limit_range_eq: limitRangeEq,

        pct_used_range: pct(pctNumer, pctDenom),

        reset_at: r.reset_at ?? null,
        last_charge_at: r.last_charge_at ?? null,
      };
    });
  }, [filteredRows, range]);


  const aggregate = useMemo(() => {
    let orgs = 0;
    let totalUsed = 0;
    let totalLimitEq = 0;

    for (const r of rows) {
      orgs += 1;

      // For "all": use used_range as all-time used, but limit eq is undefined (0)
      totalUsed += Number(r.used_range || 0);

      if (range !== 'all') {
        totalLimitEq += Number(r.limit_range_eq || 0);
      }
    }

    const overallPct =
      range === 'all'
        ? 0
        : totalLimitEq
          ? totalUsed / totalLimitEq
          : 0;

    return { orgs, totalUsed, totalLimitEq, overallPct };
  }, [rows, range]);


  const handleExportCSV = () => {
    if (!rows.length) return;

    const headers = [
      'org_id',
      'org_name',
      'credits_limit',
      'used_range',
      'pct_used_range',
      'period',
      'reset_at',
      'last_charge_at',
    ];

    const csvRows = [headers.join(',')];
    rows.forEach((r) => {
      const values = headers.map((h) => {
        let v = r[h];
        if (h === 'pct_used_range') v = (Number(v || 0) * 100).toFixed(2) + '%';
        if (h === 'reset_at') v = fmtEpoch(v);
        if (h === 'last_charge_at') v = fmtEpoch(v);
        if (typeof v === 'string') return `"${v.replace(/"/g, '""')}"`;
        return v ?? '';
      });
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');
    const encodedUri = encodeURI('data:text/csv;charset=utf-8,' + csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `api_credit_usage_${range}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      field: 'org_name',
      headerName: 'Org',
      flex: 1.2,
      sortable: false,
      renderCell: (params) => {
        if (isDiscreteMode) return <Box width="140px" height="20px" bgcolor="white" />;

        const orgId = params.row?.org_id;
        if (!orgId) return params.value;

        const apiHome = `/console/organization/${orgId}?tab=APIs`;

        return (
          <Tooltip title="Open org APIs">
            <Box
              onClick={(e) => {
                e.stopPropagation();
                navigate(apiHome);
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: 'pointer',
                fontWeight: 600,
                color: colors.greenAccent[300],
                '&:hover': {
                  textDecoration: 'underline',
                  color: colors.greenAccent[200],
                },
              }}
            >
              <span>{params.value}</span>
              <ArrowForward sx={{ fontSize: 12, opacity: 0.85 }} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'org_id',
      headerName: 'Org ID',
      flex: 0.8,
    },
    {
      field: 'plan_label',
      headerName: 'Plan',
      flex: 0.7,
    },
    {
      field: 'limit_range_eq',
      headerName: range === 'day' ? 'Limit (24h)' : range === '7d' ? 'Limit (7d)' : range === '30d' ? 'Limit (30d)' : 'Limit (All)',
      flex: 0.7,
      valueFormatter: ({ value }) => (range === 'all' ? '—' : numberFormat.format(Math.round(Number(value || 0)))),
    },
    {
      field: 'used_range',
      headerName: range === 'day' ? 'Used (24h)' : range === '7d' ? 'Used (7d)' : range === '30d' ? 'Used (30d)' : 'Used (All)',
      flex: 0.7,
      valueFormatter: ({ value }) => numberFormat.format(Math.round(Number(value || 0))),
    },
    {
      field: 'pct_used_range',
      headerName: range === 'all' ? '% of Limit (current period)' : '% of Limit',
      flex: 0.6,
      valueFormatter: ({ value }) => (range === 'all' ? '—' : `${(Number(value || 0) * 100).toFixed(2)}%`),
    },
    {
      field: 'reset_at',
      headerName: 'Reset At (UTC)',
      flex: 0.8,
      valueFormatter: ({ value }) => fmtEpoch(value),
    },
    {
      field: 'last_charge_at',
      headerName: 'Last Charge (UTC)',
      flex: 0.8,
      valueFormatter: ({ value }) => fmtEpoch(value),
    },
  ];

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error occurred while fetching API usage summary</div>;

  return (
    <Box mt={2}>
      <Header title="APIs Credit Usage" subtitle="Across all orgs with APIs enabled" />

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={3}>
          <FormControl sx={{ m: 1, minWidth: 160 }}>
            <InputLabel id="range-label">Range</InputLabel>
            <Select
              labelId="range-label"
              value={range}
              onChange={(e) => setRange(e.target.value)}
              label="Range"
            >
              <MenuItem value="day">Last 24h</MenuItem>
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="all">All time</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={5}>
          <TextField
            label="Search Orgs"
            value={orgSearch}
            onChange={(e) => setOrgSearch(e.target.value)}
            sx={{ m: 1, width: '100%', "& .MuiOutlinedInput-notchedOutline": { borderWidth: "2px" } }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', gap: 2, m: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button
              variant="outlined"
              onClick={handleExportCSV}
              sx={{ borderColor: 'lightgreen', color: 'lightgreen', '&:hover': { borderColor: 'green', color: 'green' } }}
            >
              Export CSV
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h4">Overall API Credit Stats ({range})</Typography>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={3}>
            <Typography>Orgs: {numberFormat.format(aggregate.orgs)}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography>Total Used: {numberFormat.format(Math.round(aggregate.totalUsed))}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography>
              Total Limit{range === 'all' ? '' : ` (${range})`}:{" "}
              {range === 'all' ? '—' : numberFormat.format(Math.round(aggregate.totalLimitEq))}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography>
              Overall %: {range === 'all' ? '—' : (aggregate.overallPct * 100).toFixed(2) + '%'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <DataGrid
        rows={rows}
        columns={columns}
        sx={{
          height: 650,
        }}
        disableColumnMenu
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 50, page: 0 } },
          sorting: { sortModel: [{ field: 'used_range', sort: 'desc' }] }
        }}
      />
    </Box>
  );
};

export default ApiCountSummaries;
